import type { GateDefinition } from '../scene/fieldSchema';
import type { Vec3 } from '../sim/mathx';

export interface LapState {
  currentLapStart: number | null;
  lastLapTime: number | null;
  bestLapTime: number | null;
  lapCount: number;
}

export interface LapTimerOptions {
  startGateId: string;
  finishGateId: string;
}

export type LapTimerEvent =
  | { type: 'gate.passed'; gateId: string; simTime: number }
  | { type: 'lap.completed'; lapTime: number; lapCount: number; simTime: number };

interface TriggerGate {
  id: string;
  center: Vec3;
  radius: number;
}

const squaredDistance = (a: Vec3, b: Vec3): number => {
  const x = a.x - b.x;
  const y = a.y - b.y;
  const z = a.z - b.z;
  return x * x + y * y + z * z;
};

/** First intersection parameter for a segment and sphere, or null when they miss. */
const segmentSphereIntersection = (
  from: Vec3,
  to: Vec3,
  center: Vec3,
  radius: number,
): number | null => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const fx = from.x - center.x;
  const fy = from.y - center.y;
  const fz = from.z - center.z;
  const a = dx * dx + dy * dy + dz * dz;
  if (a < 1e-12) return squaredDistance(from, center) <= radius * radius ? 0 : null;
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const c = fx * fx + fy * fy + fz * fz - radius * radius;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  const near = (-b - root) / (2 * a);
  const far = (-b + root) / (2 * a);
  if (near >= 0 && near <= 1) return near;
  if (far >= 0 && far <= 1) return far;
  return null;
};

/** Pure, deterministic gate-order lap timer with swept trigger detection. */
export class LapTimer {
  private readonly gates: readonly TriggerGate[];
  private readonly orderedGateIds: readonly string[];
  private readonly insideGateIds = new Set<string>();
  private previousPosition: Vec3 | null = null;
  private previousTime: number | null = null;
  private expectedIndex = 0;
  private lapState: LapState = {
    currentLapStart: null,
    lastLapTime: null,
    bestLapTime: null,
    lapCount: 0,
  };

  constructor(gates: readonly GateDefinition[], options: LapTimerOptions) {
    if (gates.length === 0) throw new RangeError('LapTimer requires at least one gate');
    const ids = gates.map((gate) => gate.id);
    if (new Set(ids).size !== ids.length) throw new RangeError('LapTimer gate ids must be unique');
    const startIndex = ids.indexOf(options.startGateId);
    const finishIndex = ids.indexOf(options.finishGateId);
    if (startIndex < 0)
      throw new RangeError(`LapTimer start gate not found: ${options.startGateId}`);
    if (finishIndex < 0)
      throw new RangeError(`LapTimer finish gate not found: ${options.finishGateId}`);

    this.gates = gates.map((gate) => ({
      id: gate.id,
      center: { x: gate.position[0], y: gate.position[1], z: gate.position[2] },
      // A spherical trigger is orientation-independent and fits inside the opening.
      radius: Math.min(gate.innerSize[0], gate.innerSize[1]) / 2,
    }));
    this.orderedGateIds = this.buildOrder(ids, startIndex, finishIndex);
  }

  update(scoutPos: Vec3, simTime: number): LapTimerEvent[] {
    if (![scoutPos.x, scoutPos.y, scoutPos.z, simTime].every(Number.isFinite) || simTime < 0) {
      throw new RangeError('LapTimer.update requires a finite position and simTime >= 0');
    }
    if (this.previousTime !== null && simTime < this.previousTime) {
      throw new RangeError('LapTimer.update simTime must be monotonic');
    }

    const crossings: Array<{ gate: TriggerGate; alongSegment: number }> = [];
    const nextInside = new Set<string>();
    for (const gate of this.gates) {
      const isInside = squaredDistance(scoutPos, gate.center) <= gate.radius * gate.radius;
      if (isInside) nextInside.add(gate.id);
      if (this.insideGateIds.has(gate.id)) continue;
      if (isInside) {
        crossings.push({ gate, alongSegment: 1 });
        continue;
      }
      if (this.previousPosition !== null) {
        const intersection = segmentSphereIntersection(
          this.previousPosition,
          scoutPos,
          gate.center,
          gate.radius,
        );
        if (intersection !== null) crossings.push({ gate, alongSegment: intersection });
      }
    }
    crossings.sort((a, b) => a.alongSegment - b.alongSegment);

    const events: LapTimerEvent[] = [];
    for (const { gate } of crossings) {
      events.push({ type: 'gate.passed', gateId: gate.id, simTime });
      this.advance(gate.id, simTime, events);
    }
    this.insideGateIds.clear();
    nextInside.forEach((id) => this.insideGateIds.add(id));
    this.previousPosition = { ...scoutPos };
    this.previousTime = simTime;
    return events;
  }

  state(): LapState {
    return { ...this.lapState };
  }

  private buildOrder(ids: readonly string[], startIndex: number, finishIndex: number): string[] {
    const order = [ids[startIndex]!];
    let index = startIndex;
    do {
      index = (index + 1) % ids.length;
      order.push(ids[index]!);
    } while (index !== finishIndex && index !== startIndex);
    return order;
  }

  private advance(gateId: string, simTime: number, events: LapTimerEvent[]): void {
    const startGateId = this.orderedGateIds[0]!;
    const expectedGateId = this.orderedGateIds[this.expectedIndex];
    if (gateId !== expectedGateId) {
      this.expectedIndex = 0;
      this.lapState.currentLapStart = null;
      if (gateId !== startGateId) return;
    }

    if (gateId === startGateId && this.expectedIndex === 0) {
      this.lapState.currentLapStart = simTime;
      this.expectedIndex = 1;
      return;
    }

    this.expectedIndex += 1;
    if (this.expectedIndex < this.orderedGateIds.length) return;
    const lapTime = Math.max(0, simTime - this.lapState.currentLapStart!);
    this.lapState.lastLapTime = lapTime;
    this.lapState.bestLapTime =
      this.lapState.bestLapTime === null ? lapTime : Math.min(this.lapState.bestLapTime, lapTime);
    this.lapState.lapCount += 1;
    const sharedStartFinish =
      this.orderedGateIds.length > 1 &&
      this.orderedGateIds[0] === this.orderedGateIds[this.orderedGateIds.length - 1];
    this.lapState.currentLapStart = sharedStartFinish ? simTime : null;
    this.expectedIndex = sharedStartFinish ? 1 : 0;
    events.push({
      type: 'lap.completed',
      lapTime,
      lapCount: this.lapState.lapCount,
      simTime,
    });
  }
}
